import { Box, Divider, Typography, Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import UserTokensTable from './UserTokensTable';
import { getUserTokensBalances, InsertByAdminUpdateUserTokensRequestAsync } from '../../../../../../hooks/actions/admin';
import { useTranslationContext } from '../../../../../../hooks/translations/translationsProvider';
import { AdminUserTokensBalances } from '../../../../../../models/admin';
import settings from '../../../../../../utilities/settings';
import TokensForm from './TokensForm';
import { GREEN_MAIN, GREEN_LOGO } from '../../../../../../theme/colors';

interface InsertByAdminUpdateUserTokensComponentProps {
  userId: string;
  userName?: string;
}

const InsertByAdminUpdateUserTokensComponent: React.FC<InsertByAdminUpdateUserTokensComponentProps> = ({userId, userName}) => {
  const [mainError, setMainError] = useState<string | null>(null);
  const [balances, setBalances] = useState<AdminUserTokensBalances | null>(null)
  const [reloadKey, setReloadKey] = useState(0);
  const {t} = useTranslationContext();

  const fetchBalances = useCallback(() => {
    setMainError(null);
    getUserTokensBalances(userId).then(resp => {
      setBalances(resp);
    }).catch(error => {
      const errorMex =  error?.response?.data?.message ||
                        error?.response?.data ||
                        error?.response ||
                        error?.message ||
                        error ||
                        t?.("Error while requesting data")
      console.error(errorMex)
      setMainError(errorMex);
    });
  }, [userId]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const handleCreditUpdate = async (amount: number, info: string) => {
    try {
      await InsertByAdminUpdateUserTokensRequestAsync(userId, amount, info);
      fetchBalances(); // Refresh balances after update
      setReloadKey(prevKey => prevKey + 1);
    } catch (error:any) {
      const errorMex =  error?.response?.data?.message ||
                        error?.response?.data ||
                        error?.response ||
                        error?.message ||
                        error ||
                        t?.("Error while updating credits")
      console.error(errorMex)
      setMainError(errorMex);
    }
  };

  return (
    <div>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{
            backgroundColor: `${GREEN_LOGO}05`,
            border: `1px solid ${GREEN_LOGO}20`,
            borderRadius: '8px',
            padding: 3,
            textAlign: 'center'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: GREEN_MAIN, 
                fontWeight: 'bold',
                mb: 2
              }}
            >
              {t("Balance Disponibile")}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                color: GREEN_MAIN, 
                fontWeight: 'bold'
              }}
            >
              {balances?.tokensBalance.toFixed(settings.Token.Decimals)} ASSO
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{
            backgroundColor: `${GREEN_LOGO}05`,
            border: `1px solid ${GREEN_LOGO}20`,
            borderRadius: '8px',
            padding: 3,
            textAlign: 'center'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: GREEN_MAIN, 
                fontWeight: 'bold',
                mb: 2
              }}
            >
              {t("Balance Bloccato")}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                color: GREEN_MAIN, 
                fontWeight: 'bold'
              }}
            >
              {balances?.tokensLockedBalance.toFixed(settings.Token.Decimals)} ASSO
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <TokensForm 
        onSubmit={handleCreditUpdate} 
        userName={userName} 
        currentBalance={balances?.tokensBalance || 0}
      />
      
      <Divider sx={{margin:"40px 0 30px 0"}} />
      
      <Box sx={{ mt: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: GREEN_MAIN, 
            fontWeight: 'bold',
            mb: 2
          }}
        >
          {t('Tokens ASSO - Storico Utente')}
        </Typography>
        <UserTokensTable userId={userId} reloadData={reloadKey}/>
      </Box>

      {mainError && 
        <>
          <Typography
            variant="body2" 
            color="error"   
            sx={{
                maxHeight: '6.5em',        // Limite massimo di altezza per circa 5 righe (1.3em per linea)
                overflow: 'hidden',        // Nasconde l'overflow
                textOverflow: 'ellipsis',  // Aggiunge i puntini di sospensione se il testo è troppo lungo
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 5,        // Limita il numero massimo di righe a 5
                lineHeight: '1.3em',       // Altezza della riga per calcolare il numero di righe
                wordWrap: 'break-word',    // Va a capo automaticamente
                paddingTop:'20px'
            }}
          >
            {mainError}
          </Typography>
        </>
      }
    </div>
  );
};

export default InsertByAdminUpdateUserTokensComponent;
